import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { AlertTriangle, MapPin, Shield } from 'lucide-react';

const API = "https://safesheatlas.com/api";

const SafetyHeatmap = ({ destinationId, center }) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMapData();
  }, [destinationId]);

  const fetchMapData = async () => {
    try {
      const response = await axios.get(`${API}/map/heatmap/${destinationId}`);
      setMapData(response.data);
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getZoneColor = (zoneType) => {
    switch (zoneType) {
      case 'safe': return '#10B981';
      case 'moderate': return '#14B8A6';
      case 'caution': return '#F59E0B';
      case 'avoid': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div className="h-96 bg-gray-100 rounded-xl flex items-center justify-center">
        <p className="text-gray-500">Loading map...</p>
      </div>
    );
  }

  if (!center) {
    return (
      <div className="h-96 bg-gray-100 rounded-xl flex items-center justify-center">
        <p className="text-gray-500">Map coordinates not available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="safety-heatmap">
      <div className="h-96 rounded-xl overflow-hidden shadow-lg">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Safety Zones */}
          {mapData?.safety_zones?.map((zone, idx) => (
            <Circle
              key={`zone-${idx}`}
              center={[zone.latitude, zone.longitude]}
              radius={zone.radius_meters || 500}
              pathOptions={{
                color: getZoneColor(zone.zone_type),
                fillColor: getZoneColor(zone.zone_type),
                fillOpacity: 0.2
              }}
            >
              <Popup>
                <div className="p-2">
                  <strong>{zone.zone_name}</strong>
                  <p className="text-sm mt-1">{zone.description}</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs mt-2 ${zone.zone_type === 'safe' ? 'bg-green-100 text-green-800' : zone.zone_type === 'avoid' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {zone.zone_type}
                  </span>
                </div>
              </Popup>
            </Circle>
          ))}

          {/* Incidents */}
          {mapData?.incidents?.map((incident, idx) => (
            incident.latitude && incident.longitude && (
              <CircleMarker
                key={`incident-${idx}`}
                center={[incident.latitude, incident.longitude]}
                radius={8}
                pathOptions={{
                  color: getSeverityColor(incident.severity),
                  fillColor: getSeverityColor(incident.severity),
                  fillOpacity: 0.7
                }}
              >
                <Popup>
                  <div className="p-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600 mt-1" />
                      <div>
                        <strong className="text-sm">Incident Cluster</strong>
                        <p className="text-xs mt-1">{incident.headline}</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs mt-2 ${incident.severity === 'high' ? 'bg-red-100 text-red-800' : incident.severity === 'medium' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                          {incident.severity} severity
                        </span>
                      </div>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            )
          ))}

          {/* User Reports */}
          {mapData?.user_reports?.map((report, idx) => (
            report.latitude && report.longitude && (
              <CircleMarker
                key={`report-${idx}`}
                center={[report.latitude, report.longitude]}
                radius={6}
                pathOptions={{
                  color: '#8B5CF6',
                  fillColor: '#8B5CF6',
                  fillOpacity: 0.6
                }}
              >
                <Popup>
                  <div className="p-2">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-purple-600 mt-1" />
                      <div>
                        <strong className="text-sm">User Report</strong>
                        <p className="text-xs mt-1">{report.location_detail}</p>
                        <span className="inline-block px-2 py-1 rounded text-xs mt-2 bg-purple-100 text-purple-800">
                          {report.report_type}
                        </span>
                      </div>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            )
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg p-4 shadow-md">
        <h4 className="font-semibold mb-3 text-sm">Map Legend</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Zone Colors */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Safety Zones</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10B981', opacity: 0.6 }}></div>
                <span className="text-xs">Safe Areas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#14B8A6', opacity: 0.6 }}></div>
                <span className="text-xs">Moderate Safety</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B', opacity: 0.6 }}></div>
                <span className="text-xs">Caution Needed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#EF4444', opacity: 0.6 }}></div>
                <span className="text-xs">Avoid Areas</span>
              </div>
            </div>
          </div>
          
          {/* Incident Markers */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Incident Markers</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EF4444' }}></div>
                <span className="text-xs">High Risk Incidents</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }}></div>
                <span className="text-xs">Medium Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
                <span className="text-xs">Low Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8B5CF6' }}></div>
                <span className="text-xs">User Reports</span>
              </div>
            </div>
          </div>
        </div>
        
        {(!mapData?.incidents?.length && !mapData?.safety_zones?.length && !mapData?.user_reports?.length) && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <Shield className="w-4 h-4 inline mr-1" />
              Heatmap data is being generated from recent incident reports. Check back soon for detailed safety zones.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyHeatmap;